import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { PHOTO_GALLERY } from "@/lib/constants";
import Logo from "@/components/Logo";

const emptyPost = {
  title: "",
  content: "",
  excerpt: "",
  category: "Poradnik",
  cover: "/photos/photo-01.jpg",
  meta_title: "",
  meta_description: "",
  published: true,
};

const LoginScreen = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Zalogowano");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink p-6">
      <div className="w-full max-w-md">
        <div className="mb-10 flex justify-center">
          <Logo />
        </div>
        <form onSubmit={submit} className="card-edge space-y-4 p-8" data-testid="admin-login-form">
          <p className="eyebrow mb-2">Panel administratora</p>
          <input required type="email" placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" className="field w-full px-5 py-3.5" />
          <input required type="password" placeholder="Hasło" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" className="field w-full px-5 py-3.5" />
          {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="admin-login-submit" className="btn-amber w-full px-6 py-3.5 font-mono text-xs font-medium uppercase tracking-[0.2em] disabled:opacity-50">
            {busy ? "Logowanie…" : "Zaloguj się"}
          </button>
        </form>
      </div>
      <Toaster position="bottom-center" theme="dark" />
    </div>
  );
};

const PostForm = ({ initial, onSave, onCancel }) => {
  const [p, setP] = useState(initial);
  const set = (k) => (e) => setP({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(p);
      }}
      className="card-edge space-y-4 p-6"
      data-testid="admin-post-form"
    >
      <p className="eyebrow">{initial.id ? `Edycja: ${initial.title}` : "Nowy wpis"}</p>
      <input required placeholder="Tytuł wpisu" value={p.title} onChange={set("title")} data-testid="post-form-title" className="field w-full px-4 py-3" />
      <div className="grid gap-4 sm:grid-cols-2">
        <select value={p.category} onChange={set("category")} data-testid="post-form-category" className="field w-full px-4 py-3">
          {["Poradnik", "Trendy", "Realizacje", "Porady"].map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={p.cover} onChange={set("cover")} data-testid="post-form-cover" className="field w-full px-4 py-3">
          {PHOTO_GALLERY.map((ph) => <option key={ph.src} value={ph.src}>{ph.src.replace("/photos/", "")}</option>)}
        </select>
      </div>
      <textarea placeholder="Zajawka (skrócony opis)" rows="2" value={p.excerpt} onChange={set("excerpt")} data-testid="post-form-excerpt" className="field w-full px-4 py-3" />
      <textarea required placeholder="Treść wpisu. Nagłówki zaczynaj od '## ', listy od '- '. Oddziel akapity pustą linią." rows="12" value={p.content} onChange={set("content")} data-testid="post-form-content" className="field w-full px-4 py-3" />
      <input placeholder="Meta title (SEO)" value={p.meta_title} onChange={set("meta_title")} data-testid="post-form-meta-title" className="field w-full px-4 py-3" />
      <textarea placeholder="Meta description (SEO, do 160 znaków)" rows="2" value={p.meta_description} onChange={set("meta_description")} data-testid="post-form-meta-desc" className="field w-full px-4 py-3" />
      <label className="flex items-center gap-3 text-sm text-pearl" data-testid="post-form-published-label">
        <input type="checkbox" checked={p.published} onChange={set("published")} data-testid="post-form-published" className="h-4 w-4 accent-[#FFB800]" />
        Opublikowany
      </label>
      <div className="flex gap-3">
        <button type="submit" data-testid="post-form-save" className="btn-amber px-6 py-3 font-mono text-xs uppercase tracking-[0.2em]">Zapisz</button>
        <button type="button" onClick={onCancel} data-testid="post-form-cancel" className="btn-ghost px-6 py-3 font-mono text-xs uppercase tracking-[0.2em]">Anuluj</button>
      </div>
    </form>
  );
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [posts, setPosts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("posts");
  const [editing, setEditing] = useState(null);

  const loadPosts = () => api.get("/admin/posts").then((r) => setPosts(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  const loadLeads = () => api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});

  useEffect(() => {
    loadPosts();
    loadLeads();
  }, []);

  const save = async (p) => {
    try {
      if (p.id) await api.put(`/posts/${p.id}`, p);
      else await api.post("/posts", p);
      toast.success("Zapisano. Mapa strony zaktualizowana.");
      setEditing(null);
      loadPosts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Usunąć wpis „${p.title}"?`)) return;
    try {
      await api.delete(`/posts/${p.id}`);
      toast.success("Wpis usunięty");
      loadPosts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-ink pb-24 pt-28" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl px-4 sm:px-8">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stonemute" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="btn-ghost px-5 py-2 font-mono text-[11px] uppercase tracking-[0.2em]">Wyloguj</button>
          </div>
        </div>

        <div className="mb-8 flex gap-3">
          <button onClick={() => setTab("posts")} data-testid="admin-tab-posts" className={`notch-sm px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] ${tab === "posts" ? "bg-amber text-ink" : "border border-white/10 bg-graph text-stonemute"}`}>Wpisy ({posts.length})</button>
          <button onClick={() => setTab("leads")} data-testid="admin-tab-leads" className={`notch-sm px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] ${tab === "leads" ? "bg-amber text-ink" : "border border-white/10 bg-graph text-stonemute"}`}>Zapytania ({leads.length})</button>
        </div>

        {tab === "posts" && (
          <div>
            {!editing && (
              <button onClick={() => setEditing({ ...emptyPost })} data-testid="admin-new-post" className="btn-amber mb-6 px-6 py-3 font-mono text-xs font-medium uppercase tracking-[0.2em]">
                + Nowy wpis
              </button>
            )}
            {editing && <PostForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />}
            <div className="mt-6 space-y-3">
              {posts.map((p) => (
                <div key={p.id} className="card-edge flex flex-wrap items-center gap-4 p-5" data-testid={`admin-post-row-${p.slug}`}>
                  {p.cover && <img src={p.cover} alt="" className="h-12 w-16 object-cover" />}
                  <div className="min-w-0 flex-1">
                    <p className="font-display truncate text-lg font-bold uppercase text-pearl">{p.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-stonemute">
                      /blog/{p.slug} · {p.category} · {p.published ? "opublikowany" : "szkic"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(p)} data-testid={`post-edit-${p.slug}`} className="btn-ghost px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em]">Edytuj</button>
                    <button onClick={() => remove(p)} data-testid={`post-delete-${p.slug}`} className="px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-red-400 hover:text-red-300">Usuń</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div className="space-y-3" data-testid="admin-leads-list">
            {leads.length === 0 && <p className="text-stonemute">Brak zapytań.</p>}
            {leads.map((l) => (
              <div key={l.id} className="card-edge p-5" data-testid={`admin-lead-${l.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-display text-lg font-bold uppercase text-pearl">{l.name} {l.city && `· ${l.city}`}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-stonemute">{l.created_at ? new Date(l.created_at).toLocaleString("pl-PL") : ""}</p>
                </div>
                <p className="mt-1 text-sm text-amber">{l.phone} {l.email && `· ${l.email}`}</p>
                <p className="mt-2 text-sm text-stonemute">{l.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
      <Toaster position="bottom-center" theme="dark" />
    </div>
  );
};

const Admin = () => {
  const { user } = useAuth();
  if (user === null) {
    return <div className="flex min-h-screen items-center justify-center bg-ink"><p className="font-mono text-xs uppercase tracking-[0.2em] text-stonemute">Sprawdzanie sesji…</p></div>;
  }
  return user ? <Dashboard /> : <LoginScreen />;
};

export default Admin;
